import * as React from 'react';
import { useRef, useState, lazy } from "react";
import { useForm, type FieldError } from 'react-hook-form';
import Dump from "../components/dump.tsx";
import Spinner from "../components/spinner";
import { clsx } from 'clsx';
import { getEnv, hasAstroEnv } from "../helpers/env.ts";

interface ContactFormValues {
	name: string,
	email: string,
	phone: string,
	company: string,
	message: string,
	subject: string,
	consent: boolean,
}

type Props = {
	content?: any
}

const DevTool = lazy(() => import('../components/hookFormDevToolsWrapper'));

export default function Contact({content = null, ...otherProps}: Props) {
	const [isSent, setIsSent] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		control,
	} = useForm<ContactFormValues>();

	const form = useRef(null);

	function inputRadioClass(error: boolean|string|FieldError = false) {
		return clsx('', (error ? '!border-secondary-300' : 'border-transparent'));
	}

	function inputClass(error: boolean|string|FieldError = false) {
		return clsx('', error && '!border-secondary-300');
	}

	return (<>
		<form ref={form} onSubmit={handleSubmit(onSubmit)} className="relative">
			{hasAstroEnv('MODE') === false && getEnv('PUBLIC_DUMP_ALLOWED') === "yes" && // hide this devtool in astro env
				<DevTool control={control} placement="top-right" styles={{button: { position: "absolute", zIndex: "auto" }}}/>
			}

			<div className="flex flex-wrap overflow-hidden max-w-screen-2xl -mx-12 text-lg pb-3">
				<div className="w-full px-12 mb-2">
					<input
						//defaultValue="Hallo"
						className={clsx('w-full', inputClass(errors.name))}
						type="text"
						id="name" name="name"
						placeholder="Name"
						size={20}
						{...register("name", {required: true})}
					/>
				</div>

				<div className="w-full px-12 mb-2">
					<input
						//defaultValue="tst@gfdgsd.pl"
						className={clsx('w-full', inputClass(errors.email))}
						type="email"
						id="email" name="email"
						placeholder="Email"
						size={20}
						{...register("email", {required: true, pattern: /^\S+@\S+$/i})}
					/>
				</div>

				<div className="w-full px-12 mb-2">
					<textarea
						//defaultValue="Sample"
						className={clsx('w-full', inputClass(errors.message))}
						id="message" name="textarea"
						placeholder="Message"
						rows={8}
						{...register("message", {required: true})}
					/>
				</div>

				<div className="w-full px-12 mb-4">
					<div className={clsx('border px-4 -mx-4 flex py-1 align-middle', inputRadioClass(errors.consent))}>
						<input
							className="!mb-0 !focus:outline-0"
							id="consent" name="consent"
							type="checkbox"
							//checked
							{...register("consent", {required: true})}
						/>
						<label className="mb-0 text-base inline-block ml-2" htmlFor="consent">
							I agree to receiving emails
						</label>
					</div>
				</div>
			</div>

			<button
				disabled={isSubmitting}
				className={clsx("w-full sm:w-auto min-w-[180px] mr-6", isSubmitting ? '!bg-grey' : '!bg-red')}
				type="submit"
			>
				{isSubmitting ? 'SENDING' : 'SUBMIT'}
				{isSubmitting && <Spinner className="!w-4 !h-4"/>}
			</button>

			{isSent && <div onClick={() => setIsSent(false)}>We will respond to the inquiry as soon as possible.</div>}
		</form>
	</>);

	function resetForm() {
		reset({
			subject: '',
			name: '',
			email: '',
			phone: '',
			company: '',
			message: '',
			consent: null,
		});
	}

	function onSubmit() {
		setIsSent(false);

		const data = new FormData(form.current);

		return fetch('/process/contact', {
			method: "POST",
			body: data
		}).then(() => {
			setIsSent(true);
			resetForm();
		});
	}
}
